/**
 * OrgFetchSection — one organisation's (HIP's) fetch on the consent detail page (R186 mockup).
 * Purely presentational: the page owns the single consent GET + polling and passes the
 * artefact's fetchStatus / recordsReceived / failureReason through as the server sent them.
 * Buttons are consent-wide (R187): Try again / Check now just call the page handlers.
 */
import React from 'react';

import { noteStyle } from './noteStyle';
import { hasRecords } from './consentOrgs';
import { formatDateTimeISTWithYear } from '../../utils/formatIST';
import {
  OTHER_ORGS, FAILURE_LINES, UNKNOWN_FAILURE_LINE, STATUS_CARD_NEW_REQUEST,
} from '../../constants/abdmCopy';

export const POLL_INTERVAL_MS = 20000;
export const PENDING_FETCH_STATUSES = new Set(['REQUESTED', 'RECEIVING']);
export const ASKABLE_FETCH_STATUSES = new Set(['NOT_REQUESTED', 'FAILED']);
export const POLL_SLOW_INTERVAL_MS = 60000;
// "Taking longer" note and the slower poll start at 30 min; polling stops at the 2 h push window (R213).
export const POLL_SLOW_AFTER_MS = 30 * 60 * 1000;
export const POLL_WINDOW_MS = 2 * 60 * 60 * 1000;
// The consent LIST refreshes at POLL_INTERVAL_MS while a request is REQUESTED, for this long at most.
export const LIST_POLL_WINDOW_MS = 30 * 60 * 1000;
const LONGER_THAN_MS = POLL_SLOW_AFTER_MS;
const STEP_INDEX = { NOT_REQUESTED: 0, REQUESTED: 1, RECEIVING: 2, RECEIVED: 3, FAILED: 3 };

const CHIPS = {
  NOT_REQUESTED: { label: 'Not requested', bg: '#f3f4f6', dark: '#1f2937', text: '#4b5563', darkText: '#9ca3af' },
  REQUESTED: { label: 'Requested', bg: '#e0f2fe', dark: '#0c4a6e', text: '#0369a1', darkText: '#bae6fd' },
  RECEIVING: { label: 'Receiving', bg: '#e0f2fe', dark: '#0c4a6e', text: '#0369a1', darkText: '#bae6fd' },
  RECEIVED: { label: 'Received', bg: '#dcfce7', dark: '#14532d', text: '#166534', darkText: '#bbf7d0' },
  FAILED: { label: 'Failed', bg: '#fee2e2', dark: '#7f1d1d', text: '#991b1b', darkText: '#fca5a5' },
};

function Steps({ status, isDark }) {
  const cur = STEP_INDEX[status] ?? 0;
  const failed = status === 'FAILED';
  const names = OTHER_ORGS.stepNames.map((n, i) => (i === 3 && failed ? OTHER_ORGS.stepFailed : n));
  const neutral = isDark ? '#475569' : '#9ca3af';
  const green = isDark ? '#16a34a' : '#4ade80';
  const red = isDark ? '#b91c1c' : '#f87171';
  const blue = '#0369a1';
  return (
    <ol style={{ display: 'flex', margin: '0 0 14px', padding: 0, listStyle: 'none' }}>
      {names.map((name, i) => {
        const done = i < cur || (i === cur && status === 'RECEIVED');
        const isCur = i === cur && !done && !failed;
        const isFail = i === cur && failed;
        const dot = isFail ? red : (done ? green : (isCur ? blue : neutral));
        return (
          <li
            key={name}
            aria-current={isCur ? 'step' : undefined}
            style={{
              flex: 1, position: 'relative', textAlign: 'center', fontSize: 12, paddingTop: 26,
              color: isFail ? (isDark ? '#fca5a5' : '#991b1b') : (done || isCur ? (isDark ? '#f5f5f7' : '#111827') : (isDark ? '#9ca3af' : '#6b7280')),
              fontWeight: isCur || isFail ? 700 : 400,
            }}
          >
            <span style={{
              position: 'absolute', top: 8, left: i === 0 ? '50%' : 0, right: i === names.length - 1 ? '50%' : 0,
              height: 3, background: isFail ? red : (done ? green : neutral),
            }}
            />
            <span style={{
              position: 'absolute', top: 2, left: '50%', marginLeft: -8, width: 16, height: 16, borderRadius: '50%',
              boxSizing: 'border-box', zIndex: 1,
              background: done || isCur || isFail ? dot : (isDark ? '#17181c' : '#ffffff'), border: `2px solid ${dot}`,
            }}
            />
            {name}
          </li>
        );
      })}
    </ol>
  );
}

export default function OrgFetchSection({
  artefact, label, active, canRequest, onAsk, onNewRequest, onCheck, onView, busy, isDark, cardStyle,
}) {
  const status = artefact.fetchStatus;
  const chip = CHIPS[status] || CHIPS.NOT_REQUESTED;
  const textMuted = isDark ? '#9ca3af' : '#6b7280';
  const headingColor = isDark ? '#f5f5f7' : '#111827';
  const pending = PENDING_FETCH_STATUSES.has(status);
  const n = artefact.recordsReceived;
  const longer = pending && artefact.requestedAt
    && Date.now() - new Date(artefact.requestedAt).getTime() > LONGER_THAN_MS;
  const named = artefact.recordsNamed;
  // Count line: "1 of N" when the organisation named N records (never "0 received" for an empty fetch).
  const countLine = n > 0 && named > 0 ? OTHER_ORGS.receivedOf(n, named) : OTHER_ORGS.receivedCount(n);
  // failure_code wins over the legacy failure_reason. An unmapped code counts as no code (fail.UNKNOWN);
  // "nothing sent" is never shown once records arrived.
  const code = FAILURE_LINES[artefact.failureCode] ? artefact.failureCode : null;
  const codeLine = code && !(n > 0 && code === 'NOTHING_SENT') ? FAILURE_LINES[code] : null;
  const emptyDone = status === 'RECEIVED' && !(n > 0);
  const consentEnded = code === 'CONSENT_ENDED';
  const showRecords = active && hasRecords(artefact);

  const note = (kind) => noteStyle(kind, isDark);
  const btn = (primary, disabled) => ({
    background: primary ? '#0369a1' : (isDark ? '#1e293b' : '#f1f5f9'),
    color: primary ? '#ffffff' : (isDark ? '#f1f5f9' : '#374151'),
    border: `1px solid ${primary ? '#0369a1' : (isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db')}`,
    borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600,
    opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
  });
  const stamps = (
    <span style={{ fontSize: 12, color: textMuted }}>
      {artefact.requestedAt && `${OTHER_ORGS.askedAt} ${formatDateTimeISTWithYear(artefact.requestedAt)}`}
      {artefact.lastCheckedAt && ` · ${OTHER_ORGS.lastChecked} ${formatDateTimeISTWithYear(artefact.lastCheckedAt)}`}
    </span>
  );

  return (
    <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }} data-testid={`org-${artefact.artefactId}`}>
      <style>{'@keyframes okSlide{0%{margin-left:-35%}100%{margin-left:100%}}'}</style>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '14px 20px',
        background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
      }}
      >
        <h2 style={{ fontSize: 15, fontWeight: 700, color: headingColor, margin: 0 }}>{label}</h2>
        {artefact.isOwnOrganisation ? (
          <span style={{
            fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 7px',
            background: isDark ? '#0c4a6e' : '#e0f2fe', color: isDark ? '#bae6fd' : '#0c4a6e',
            border: `1px solid ${isDark ? '#0369a1' : '#7dd3fc'}`,
          }}
          >
            {OTHER_ORGS.thisOrganisation}
          </span>
        ) : (
          <span style={{
            padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, letterSpacing: '0.03em',
            background: isDark ? chip.dark : chip.bg, color: isDark ? chip.darkText : chip.text,
          }}
          >
            {chip.label}
          </span>
        )}
      </div>

      <div style={{ padding: '16px 20px' }}>
        {artefact.isOwnOrganisation && (
          <div style={{ ...note('info'), marginTop: 0 }}>
            {OTHER_ORGS.ownOrgText}
          </div>
        )}

        {!artefact.isOwnOrganisation && <Steps status={status} isDark={isDark} />}

        {!artefact.isOwnOrganisation && status === 'NOT_REQUESTED' && (
          <p style={{ margin: 0, fontSize: 13, color: textMuted }}>{OTHER_ORGS.notRequestedHint}</p>
        )}

        {pending && !longer && (
          <>
            <div style={{ fontWeight: 600, fontSize: 14, color: headingColor }}>
              {n > 0 ? OTHER_ORGS.receivedSoFar(n) : OTHER_ORGS.waitingFor(label)}
            </div>
            <div style={{ height: 6, background: isDark ? '#2a2d36' : '#e5e7eb', borderRadius: 9, overflow: 'hidden', margin: '10px 0' }}>
              <div style={{ height: '100%', width: '35%', background: '#0369a1', borderRadius: 9, animation: 'okSlide 1.8s infinite ease-in-out' }} />
            </div>
            <div style={{ fontSize: 13, color: textMuted }}>
              {n > 0 ? OTHER_ORGS.readArrived : OTHER_ORGS.waitingUsual}
            </div>
          </>
        )}

        {pending && longer && (
          <div style={{ ...note('warn'), marginTop: 0 }}>
            <b>{OTHER_ORGS.longerBold}</b> {OTHER_ORGS.longerRest(label)}
          </div>
        )}

        {pending && (
          <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={onCheck} disabled={busy} style={btn(false, busy)}>
              {busy ? OTHER_ORGS.checking : OTHER_ORGS.checkNow}
            </button>
            {longer && canRequest && (
              <button type="button" onClick={onAsk} disabled={busy} style={btn(false, busy)}>{OTHER_ORGS.tryAgain}</button>
            )}
            {stamps}
            {!longer && <span style={{ fontSize: 12, color: textMuted }}>· {OTHER_ORGS.checksAuto}</span>}
          </div>
        )}

        {status === 'FAILED' && (
          <>
            <div style={note('err')}>
              {n > 0 ? (
                // Partial: some records did arrive, so never "did not send the records".
                <><b>{OTHER_ORGS.partialNote(label)}</b> {(codeLine || UNKNOWN_FAILURE_LINE).rest(label)}</>
              ) : (
                <><b>{(codeLine || UNKNOWN_FAILURE_LINE).bold(label)}</b> {(codeLine || UNKNOWN_FAILURE_LINE).rest(label)}</>
              )}
            </div>
            {n > 0 && <div style={{ fontSize: 13, color: headingColor, marginTop: 8 }}>{countLine}</div>}
            <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              {canRequest && !consentEnded && (
                <button type="button" onClick={onAsk} disabled={busy} style={btn(true, busy)}>{OTHER_ORGS.tryAgain}</button>
              )}
              {canRequest && consentEnded && (
                <button type="button" onClick={onNewRequest} style={btn(true, false)}>{STATUS_CARD_NEW_REQUEST}</button>
              )}
              {stamps}
            </div>
            {canRequest && !consentEnded && <p style={{ margin: '8px 0 0', fontSize: 12, color: textMuted }}>{OTHER_ORGS.askAllHint}</p>}
          </>
        )}

        {status === 'RECEIVED' && !emptyDone && (
          <div style={{ fontSize: 13, color: headingColor }}>{countLine}</div>
        )}
        {emptyDone && (
          <div style={note('gray')}>
            <b>{(codeLine || FAILURE_LINES.NOTHING_SENT).bold(label)}</b> {(codeLine || FAILURE_LINES.NOTHING_SENT).rest(label)}
          </div>
        )}
        {status === 'RECEIVED' && !emptyDone && (codeLine || (!code && artefact.failureReason)) && (
          <div style={note('warn')}>
            {OTHER_ORGS.partialNote(label)}
          </div>
        )}

        {showRecords && (
          <div style={{ marginTop: 12 }}>
            <button
              type="button" onClick={onView}
              style={{ ...btn(true, false), background: isDark ? '#0ea5e9' : '#0369a1', color: isDark ? '#0b1220' : '#ffffff' }}
            >
              {status === 'RECEIVED' ? OTHER_ORGS.viewTheseRecords : OTHER_ORGS.viewNRecords(n)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
